import { useEffect, useMemo, useState } from 'react'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded'
import { useChat } from '../../../context/useChat'
import { useNls } from '../../../nls/nlsProvider'
import { subscribeHuddleSessions, type HuddleSession } from '../../../services/huddleSessionService'
import { subscribeMeetings } from '../../../services/meetingService'
import type { MeetingRecord } from '../../../types/meetings'
import { useToast } from '../../../context/useToast'
import { useAuth } from '../../../context/useAuth'
import { useTimeline } from '../../../context/useTimeline'
import { MemberActionCard, MemberEmptyState } from '../controls/MemberControlsPrimitives'
import { MemberNexusHero, MemberPresenceChip, MemberSurfaceFrame, type MemberNexusChip } from '../controls/MemberNexusPrimitives'
import { Button } from '../../Common/Button'

type Props = {
  workspaceId: string
  nexusChips?: MemberNexusChip[]
  onOpenChat?: () => void
}

const meetingDate = (value: MeetingRecord['scheduledAt']): Date | null => {
  if (value instanceof Date) return value
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') return (value as { toDate: () => Date }).toDate()
  const parsed = new Date(value as unknown as string)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const sessionDate = (value: HuddleSession['startedAt'] | HuddleSession['endedAt']): Date | null => {
  if (!value) return null
  if (value instanceof Date) return value
  return value.toDate()
}

const timeLabel = (date: Date) => date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })

const isScheduledHuddle = (meeting: MeetingRecord): boolean => {
  const startsAt = meetingDate(meeting.scheduledAt)
  return meeting.sourceProvider === 'HUDDLE'
    && Boolean(startsAt && startsAt.getTime() >= Date.now())
    && !['CANCELLED', 'COMPLETED'].includes(meeting.status)
}

export const MemberHuddleScreen = ({ workspaceId, nexusChips = [], onOpenChat }: Props) => {
  const { t } = useNls()
  const { showToast } = useToast()
  const { user, currentUser } = useAuth()
  const { isAccessRestricted } = useTimeline()
  const { huddlePresence, activeChannelId, channelsList, setActiveChannelId, setHuddleState } = useChat()
  const [meetings, setMeetings] = useState<MeetingRecord[]>([])
  const [sessions, setSessions] = useState<HuddleSession[]>([])

  const uid = user?.uid ?? currentUser?.uid ?? ''
  const isInHuddle = huddlePresence.some((p) => p.uid === uid)

  useEffect(() => subscribeMeetings(workspaceId, setMeetings, () => setMeetings([]), { restricted: !!isAccessRestricted, uid }), [workspaceId, isAccessRestricted, uid])
  useEffect(() => subscribeHuddleSessions(workspaceId, setSessions, () => setSessions([]), { restricted: !!isAccessRestricted, uid }), [workspaceId, isAccessRestricted, uid])

  const scheduled = useMemo(() => meetings
    .filter(isScheduledHuddle)
    .sort((left, right) => (meetingDate(left.scheduledAt)?.getTime() ?? 0) - (meetingDate(right.scheduledAt)?.getTime() ?? 0)), [meetings])
  const recent = useMemo(() => sessions
    .filter((session) => sessionDate(session.endedAt))
    .sort((left, right) => (sessionDate(right.endedAt)?.getTime() ?? 0) - (sessionDate(left.endedAt)?.getTime() ?? 0)), [sessions])
  const live = useMemo(() => sessions
    .filter((session) => !sessionDate(session.endedAt))
    .sort((left, right) => (sessionDate(right.startedAt)?.getTime() ?? 0) - (sessionDate(left.startedAt)?.getTime() ?? 0)), [sessions])

  const joinHuddle = async (requestedChannelId?: string) => {
    const channelId = requestedChannelId ?? activeChannelId ?? channelsList[0]?.id
    if (!channelId) {
      onOpenChat?.()
      showToast(t('NLSMemberHuddleOpenChatFirst'), 'ghost')
      return
    }
    if (activeChannelId !== channelId) setActiveChannelId(channelId)
    try {
      await setHuddleState({ isListening: true, isSpeaking: false }, channelId)
      showToast(t('NLSMemberHuddleStarted'), 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('NLSMemberHuddleJoinFailed'), 'error')
    }
  }

  const leaveHuddle = async (channelId: string) => {
    try {
      await setHuddleState({ isListening: false, isSpeaking: false }, channelId)
      showToast(t('NLSMemberHuddleLeft'), 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('NLSMemberHuddleJoinFailed'), 'error')
    }
  }

  return (
    <MemberSurfaceFrame className="memberHuddleMobile" testId="member-huddle-screen">
      <MemberNexusHero
        eyebrow={t('NLSMemberHuddleEyebrow')}
        title={new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
        chips={nexusChips}
      />

      <section className="memberNexusSection">
        <div className="memberNexusSection__header">
          <span>{t('NLSMemberHuddleLive')}</span>
          <span>{live.length}</span>
        </div>
        {live.length > 0 ? (
          <div className="memberHuddleCluster">
            {live.map((session) => {
              const isActiveChannel = session.channelId === activeChannelId
              const showLeave = isActiveChannel && isInHuddle
              return <div key={session.id}>
                <MemberActionCard
                  icon={<SensorsRoundedIcon />}
                  title={session.channelName || t('NLSMemberHuddleUntitledChannel')}
                  status={t('NLSMemberHuddleLive')}
                  statusTone="success"
                  onClick={() => void (showLeave ? leaveHuddle(session.channelId) : joinHuddle(session.channelId))}
                  actions={<Button variant="secondary" size="md" onClick={() => void (showLeave ? leaveHuddle(session.channelId) : joinHuddle(session.channelId))}>{showLeave ? t('NLSMemberHuddleLeave') : t('NLSMemberHuddleJoin')}</Button>}
                />
                {isActiveChannel && huddlePresence.length > 0 ? <>
                  <MemberPresenceChip count={huddlePresence.length} label={t('NLSMemberHuddleInHuddle')} />
                  {huddlePresence.map((entry) => (
                    <div key={entry.uid} className="memberHuddlePerson">
                      {entry.photoURL ? <img src={entry.photoURL} alt="" /> : <span>{entry.displayName.slice(0, 2).toUpperCase()}</span>}
                      <strong>{entry.displayName}</strong>
                      <small>{entry.isSpeaking ? t('NLSMemberHuddleSpeaking') : t('NLSMemberHuddleListening')}</small>
                    </div>
                  ))}
                </> : null}
              </div>
            })}
          </div>
        ) : (
          <MemberEmptyState
            icon={<SensorsRoundedIcon />}
            title={t('NLSMemberHuddleNoLiveTitle')}
            hint={t('NLSMemberHuddleNoLiveHint')}
            cta={<Button variant="primary" size="lg" onClick={() => void joinHuddle()}>{t('NLSMemberHuddleStart')}</Button>}
          />
        )}
      </section>

      <section className="memberNexusSection">
        <div className="memberNexusSection__header"><span>{t('NLSMemberHuddleScheduled')}</span><span>{scheduled.length}</span></div>
        {scheduled.length > 0 ? (
          <div className="memberNexusCardList">
            {scheduled.map((meeting) => {
              const startsAt = meetingDate(meeting.scheduledAt)
              return <MemberActionCard key={meeting.id} icon={<ScheduleRoundedIcon />} title={meeting.title || t('NLSMemberHuddleUntitledMeeting')} sub={startsAt ? timeLabel(startsAt) : ''} status={t('NLSMemberHuddleScheduled')} statusTone="info" />
            })}
          </div>
        ) : <MemberEmptyState icon={<ScheduleRoundedIcon />} title={t('NLSMemberHuddleNoScheduled')} />}
      </section>

      <section className="memberNexusSection">
        <div className="memberNexusSection__header"><span>{t('NLSMemberHuddleRecent')}</span><span>{recent.length}</span></div>
        {recent.length > 0 ? (
          <div className="memberNexusCardList">
            {recent.map((session) => {
              const startedAt = sessionDate(session.startedAt)
              const endedAt = sessionDate(session.endedAt)
              return <MemberActionCard key={session.id} icon={<HistoryRoundedIcon />} title={session.channelName || t('NLSMemberHuddleUntitledChannel')} sub={startedAt && endedAt ? `${timeLabel(startedAt)} - ${timeLabel(endedAt)}` : ''} status={t('NLSMemberHuddleCompleted')} statusTone="info" />
            })}
          </div>
        ) : <MemberEmptyState icon={<HistoryRoundedIcon />} title={t('NLSMemberHuddleNoRecent')} />}
      </section>
    </MemberSurfaceFrame>
  )
}
